import React, { useState } from 'react';
import { MenuItem, Alert, AlertColor, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Modal from 'components/common/Modal';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import {
  ALLOWED_STATUSES_TO_SUBMIT,
  TwilioBusinessRegistrationIdentifier,
  TwilioBusinessType,
} from 'constants/twilio/twilioCustomerProfile';
import { CampaignProfile } from 'types/twilio/campaignProfile';
import {
  CreateTwilioCustomerProfileOptions,
  TwilioCustomerProfileEvaluationType,
} from 'types/twilio/twilioCustomerProfile';
import { buildDefaultCustomerProfileOptions } from 'utils/twilioCustomerProfile';
import { CampaignDescriptionField } from '../components/CampaignDescriptionField';
import { useCampaignProfile } from '../hooks/useCampaignProfile';
import { TwilioCustomerProfileEvaluation } from '../customerProfileEvaluation/TwilioCustomerProfileEvaluation';
import { useEvaluateTwilioCustomerProfile } from '../mutations/useEvaluateTwilioCustomerProfile';
import { useSubmitTwilioCustomerProfile } from '../mutations/useSubmitTwilioCustomerProfile';
import { useGetTwilioCustomerProfile } from '../queries/useGetTwilioCustomerProfile';

interface TwilioCustomerProfileProps {
  twilioCustomerProfileId: string | null | undefined;
  businessId: number;
  businessName: string;
  businessWebsiteUrl: string;
  savedCampaignProfile: CampaignProfile | null | undefined;
  handleCreateCustomerProfileClickEvent: (opts: CreateTwilioCustomerProfileOptions) => void;
}

export const TwilioCustomerProfile = ({
  twilioCustomerProfileId,
  businessName,
  businessWebsiteUrl,
  savedCampaignProfile,
  businessId,
  handleCreateCustomerProfileClickEvent,
}: TwilioCustomerProfileProps) => {
  const { data: twilioCustomerProfile, isLoading: isTwilioCustomerProfileLoading } =
    useGetTwilioCustomerProfile(businessId, twilioCustomerProfileId);
  const {
    mutate: submitTwilioCustomerProfile,
    isLoading: isSubmittingTwilioCustomerProfileLoading,
  } = useSubmitTwilioCustomerProfile();
  const {
    mutateAsync: evaluateTwilioCustomerProfile,
    isLoading: isTwilioCustomerProfileEvaluationLoading,
  } = useEvaluateTwilioCustomerProfile();

  const [customerProfileEvaluation, setCustomerProfileEvaluation] =
    useState<TwilioCustomerProfileEvaluationType | null>(null);
  const [customerProfileOptions, setCustomerProfileOptions] =
    useState<CreateTwilioCustomerProfileOptions>(
      buildDefaultCustomerProfileOptions({ savedCampaignProfile, businessName, businessWebsiteUrl }),
    );

  const handleInputChange =
    (field: keyof CreateTwilioCustomerProfileOptions) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCustomerProfileOptions((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleEvaluateCustomerProfileClickEvent = async () => {
    const { customerProfileEvaluation } = await evaluateTwilioCustomerProfile({
      businessId,
      customerProfileId: twilioCustomerProfileId!,
    });

    setCustomerProfileEvaluation(customerProfileEvaluation);
  };

  const {
    campaignDescription,
    onCampaignDescriptionChange,
    trimmedCampaignDescriptionLength,
    isCampaignDescriptionValid,
    isSavingCampaignProfile,
    handleSaveCampaignProfile,
    resetCampaignDescription,
  } = useCampaignProfile({
    businessId,
    savedCampaignProfile,
    customerProfileOptions,
  });

  const handleCreateCustomerProfile = async () => {
    try {
      await handleSaveCampaignProfile();
    } catch (error) {
      // Error is already handled in the mutation's onError callback, so we just return early here.
      return;
    }

    handleCreateCustomerProfileClickEvent(customerProfileOptions);
  };

  const customerProfileStatusColor: AlertColor =
    twilioCustomerProfile?.customerProfile?.status === 'twilio-approved'
      ? 'success'
      : twilioCustomerProfile?.customerProfile?.status === 'twilio-rejected'
      ? 'error'
      : 'info';

  return (
    <div>
      {!!twilioCustomerProfileId && isTwilioCustomerProfileLoading && <CentsLoader />}

      {!twilioCustomerProfileId && (
        <div className="d-flex flex-column" style={{ gap: '10px' }}>
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Legal business name"
            onChange={handleInputChange('legalBusinessName')}
            value={customerProfileOptions.legalBusinessName}
            fullWidth
          />

          <TextField
            type="text"
            select
            variant="outlined"
            color="secondary"
            label="Business type"
            onChange={handleInputChange('businessType')}
            value={customerProfileOptions.businessType}
            fullWidth
          >
            {Object.values(TwilioBusinessType).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Business website"
            onChange={handleInputChange('businessWebSite')}
            value={customerProfileOptions.businessWebSite}
            fullWidth
          />

          <TextField
            type="text"
            select
            variant="outlined"
            color="secondary"
            label="Business registration ID type"
            onChange={handleInputChange('businessRegistrationIdentifier')}
            value={customerProfileOptions.businessRegistrationIdentifier}
            fullWidth
          >
            {Object.values(TwilioBusinessRegistrationIdentifier).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Business Registration Number"
            onChange={handleInputChange('businessRegistrationNumber')}
            value={customerProfileOptions.businessRegistrationNumber}
            fullWidth
          />

          <CampaignDescriptionField
            value={campaignDescription}
            onChange={onCampaignDescriptionChange}
            trimmedLength={trimmedCampaignDescriptionLength}
            isValid={isCampaignDescriptionValid}
            onReset={resetCampaignDescription}
          />

          <div className="d-flex" style={{ gap: '10px', paddingLeft: '10px' }}>
            <Button
              variant="outlined"
              style={{ margin: 0 }}
              disabled={isSavingCampaignProfile || !isCampaignDescriptionValid}
              onClick={handleSaveCampaignProfile}
            >
              Save
            </Button>

            <Button
              variant="outlined"
              style={{ margin: 0 }}
              disabled={isSavingCampaignProfile || !isCampaignDescriptionValid}
              onClick={handleCreateCustomerProfile}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {!!twilioCustomerProfile?.customerProfile && (
        <div style={{ marginLeft: '10px', gap: '10px' }} className="d-flex flex-wrap">
          <Grid container spacing={2} className="w-100">
            <Grid size="auto" display="flex" alignItems="center">
              Customer Profile Status
            </Grid>
            <Grid size="auto">
              <Alert severity={customerProfileStatusColor} icon={false}>
                {twilioCustomerProfile.customerProfile?.status}
              </Alert>
            </Grid>
          </Grid>

          {twilioCustomerProfile.customerProfile.status !== 'twilio-approved' && (
            <>
              <Button
                variant="outlined"
                style={{ margin: 0 }}
                onClick={handleEvaluateCustomerProfileClickEvent}
                disabled={
                  isSubmittingTwilioCustomerProfileLoading ||
                  isTwilioCustomerProfileEvaluationLoading
                }
              >
                Evaluate
              </Button>

              {ALLOWED_STATUSES_TO_SUBMIT.includes(
                twilioCustomerProfile.customerProfile.status,
              ) && (
                <Button
                  color="success"
                  variant="outlined"
                  style={{ margin: 0 }}
                  disabled={isSubmittingTwilioCustomerProfileLoading}
                  onClick={() =>
                    submitTwilioCustomerProfile({
                      businessId,
                      customerProfileId: twilioCustomerProfile.customerProfile!.sid,
                    })
                  }
                >
                  Submit
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {!!customerProfileEvaluation && (
        <Modal onClose={() => setCustomerProfileEvaluation(null)} modalHeight={'auto'}>
          <TwilioCustomerProfileEvaluation customerProfileEvaluation={customerProfileEvaluation} />
        </Modal>
      )}
    </div>
  );
};
